import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Shield, Users, FileText, Settings, LogOut, ArrowLeft,
  Loader2, Plus, Trash2, ToggleLeft, ToggleRight, Tag, Star, MessageSquare, Mail, Send, X
} from 'lucide-react';
import api from '../utils/api';

const AdminVouchers = () => {
  const navigate = useNavigate();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [newVoucher, setNewVoucher] = useState({
    code: '', discountPercent: 50, description: '', maxUses: 0,
    validFrom: '', validUntil: '', applicablePlans: []
  });

  useEffect(() => { fetchVouchers(); }, []);

  const fetchVouchers = async () => {
    try {
      const data = await api.admin.getVouchers();
      setVouchers(data.vouchers || []);
    } catch (err) {
      console.error('Failed to fetch vouchers:', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });
    try {
      await api.admin.createVoucher(newVoucher);
      setMessage({ type: 'success', text: 'Voucher created successfully.' });
      setShowCreate(false);
      setNewVoucher({ code: '', discountPercent: 50, description: '', maxUses: 0, validFrom: '', validUntil: '', applicablePlans: [] });
      fetchVouchers();
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to create voucher.' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id, isActive) => {
    try {
      await api.admin.updateVoucher(id, { isActive: !isActive });
      fetchVouchers();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this voucher?')) return;
    try {
      await api.admin.deleteVoucher(id);
      fetchVouchers();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  // Email sending state
  const [sendingEmailFor, setSendingEmailFor] = useState(null); // voucher ID being emailed
  const [emailAddress, setEmailAddress] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  const handleSendEmail = async (voucherId) => {
    if (!emailAddress.trim() || !emailAddress.includes('@')) {
      setMessage({ type: 'error', text: 'Please enter a valid email address.' });
      return;
    }
    setEmailSending(true);
    setMessage({ type: '', text: '' });
    try {
      const result = await api.admin.sendVoucherEmail(voucherId, emailAddress.trim());
      setMessage({ type: 'success', text: result.message || `Voucher emailed to ${emailAddress.trim()}` });
      setSendingEmailFor(null);
      setEmailAddress('');
    } catch (err) {
      setMessage({ type: 'error', text: err.message || 'Failed to send email.' });
    } finally {
      setEmailSending(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    navigate('/admin-portal-7x9k2m');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 text-red-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900">
      <header className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-600 rounded-lg">
              <Shield className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Admin Portal</h1>
              <p className="text-xs text-gray-400">Due Diligence Platform</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/dashboard" className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white transition-colors">
              <ArrowLeft className="h-4 w-4" />
              User Portal
            </Link>
            <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 text-gray-400 hover:text-white transition-colors">
              <LogOut className="h-4 w-4" />
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <nav className="bg-gray-800/50 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-1 py-2">
            {[
              { to: '/admin-portal-7x9k2m/dashboard', icon: Shield, label: 'Dashboard' },
              { to: '/admin-portal-7x9k2m/reports', icon: FileText, label: 'Reports' },
              { to: '/admin-portal-7x9k2m/users', icon: Users, label: 'Users' },
              { to: '/admin-portal-7x9k2m/config', icon: Settings, label: 'Config' },
              { to: '/admin-portal-7x9k2m/vouchers', icon: Tag, label: 'Vouchers' },
              { to: '/admin-portal-7x9k2m/reviews', icon: Star, label: 'Reviews' },
              { to: '/admin-portal-7x9k2m/audit', icon: Shield, label: 'Audit' },
              { to: '/admin-portal-7x9k2m/api-keys', icon: Shield, label: 'API Keys' },
            ].map(item => (
              <Link key={item.to} to={item.to}
                className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                  item.label === 'Vouchers' ? 'bg-gray-700 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                }`}>
                <item.icon className="h-4 w-4" />{item.label}
              </Link>
            ))}
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {message.text && (
          <div className={`mb-4 p-3 rounded-md text-sm ${message.type === 'success' ? 'bg-green-900/50 text-green-300 border border-green-700' : 'bg-red-900/50 text-red-300 border border-red-700'}`}>
            {message.text}
          </div>
        )}

        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Discount Vouchers</h2>
          <button onClick={() => setShowCreate(!showCreate)}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700">
            <Plus className="h-4 w-4" /> Create Voucher
          </button>
        </div>

        {showCreate && (
          <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 mb-6">
            <h3 className="text-lg font-semibold text-white mb-4">New Voucher</h3>
            <form onSubmit={handleCreate} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Code</label>
                <input type="text" required value={newVoucher.code}
                  onChange={(e) => setNewVoucher({ ...newVoucher, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. LAUNCH50"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Discount %</label>
                <input type="number" required min="1" max="100" value={newVoucher.discountPercent}
                  onChange={(e) => setNewVoucher({ ...newVoucher, discountPercent: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Description</label>
                <input type="text" value={newVoucher.description}
                  onChange={(e) => setNewVoucher({ ...newVoucher, description: e.target.value })}
                  placeholder="Launch phase discount"
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Max Uses (0 = unlimited)</label>
                <input type="number" min="0" value={newVoucher.maxUses}
                  onChange={(e) => setNewVoucher({ ...newVoucher, maxUses: parseInt(e.target.value) })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Valid From</label>
                <input type="datetime-local" value={newVoucher.validFrom}
                  onChange={(e) => setNewVoucher({ ...newVoucher, validFrom: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white" />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1">Valid Until</label>
                <input type="datetime-local" value={newVoucher.validUntil}
                  onChange={(e) => setNewVoucher({ ...newVoucher, validUntil: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm text-gray-400 mb-2">Plan restrictions</label>
                <div className="flex flex-wrap gap-4">
                  {[
                    { value: 'single', label: 'Single Report (1 credit)' },
                    { value: 'starter', label: 'Essential Monthly (10 credits)' },
                    { value: 'professional', label: 'Professional Monthly (50 credits)' }
                  ].map(plan => (
                    <label key={plan.value} className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={newVoucher.applicablePlans.includes(plan.value)}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setNewVoucher(prev => ({
                            ...prev,
                            applicablePlans: checked
                              ? [...prev.applicablePlans, plan.value]
                              : prev.applicablePlans.filter(p => p !== plan.value)
                          }));
                        }}
                        className="h-4 w-4 rounded border-gray-600 bg-gray-700 text-red-600 focus:ring-red-500"
                      />
                      {plan.label}
                    </label>
                  ))}
                </div>
                <p className="mt-1 text-xs text-gray-500">Leave all unchecked to apply the voucher to every plan.</p>
              </div>
              <div className="md:col-span-2 flex gap-3">
                <button type="submit" disabled={saving}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-50">
                  {saving ? 'Creating...' : 'Create Voucher'}
                </button>
                <button type="button" onClick={() => setShowCreate(false)}
                  className="px-4 py-2 bg-gray-700 text-gray-300 rounded-md hover:bg-gray-600">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-900/50 text-gray-400 text-left">
                <th className="px-4 py-3">Code</th>
                <th className="px-4 py-3">Discount</th>
                <th className="px-4 py-3">Plans</th>
                <th className="px-4 py-3">Uses</th>
                <th className="px-4 py-3">Valid Period</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {vouchers.length === 0 ? (
                <tr><td colSpan="7" className="px-4 py-8 text-center text-gray-500">No vouchers yet. Create one to get started.</td></tr>
              ) : vouchers.map(v => (
                <tr key={v._id} className="text-gray-300">
                  <td className="px-4 py-3 font-mono font-bold text-white">{v.code}</td>
                  <td className="px-4 py-3">{v.discountPercent}%{v.description ? ` — ${v.description}` : ''}</td>
                  <td className="px-4 py-3 text-xs">
                    {(!v.applicablePlans || v.applicablePlans.length === 0)
                      ? <span className="text-gray-400">All plans</span>
                      : v.applicablePlans.map(p => {
                          const label = { single: 'Single', starter: 'Essential', professional: 'Professional' }[p] || p;
                          return <span key={p} className="inline-block mr-1 px-1.5 py-0.5 rounded bg-gray-700 text-gray-200">{label}</span>;
                        })}
                  </td>
                  <td className="px-4 py-3">{v.usedCount}{v.maxUses > 0 ? ` / ${v.maxUses}` : ' / ∞'}</td>
                  <td className="px-4 py-3 text-xs">
                    {v.validFrom ? new Date(v.validFrom).toLocaleDateString() : 'Any'} — {v.validUntil ? new Date(v.validUntil).toLocaleDateString() : 'No end'}
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => handleToggle(v._id, v.isActive)}
                      className={`flex items-center gap-1 text-xs px-2 py-1 rounded ${v.isActive ? 'bg-green-900/50 text-green-400' : 'bg-gray-700 text-gray-500'}`}>
                      {v.isActive ? <ToggleRight className="h-3 w-3" /> : <ToggleLeft className="h-3 w-3" />}
                      {v.isActive ? 'Active' : 'Inactive'}
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {v.isActive && (
                        <button
                          onClick={() => { setSendingEmailFor(sendingEmailFor === v._id ? null : v._id); setEmailAddress(''); }}
                          className="text-blue-400 hover:text-blue-300" title="Email voucher"
                        >
                          <Mail className="h-4 w-4" />
                        </button>
                      )}
                      <button onClick={() => handleDelete(v._id)}
                        className="text-red-400 hover:text-red-300" title="Delete voucher">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                    {sendingEmailFor === v._id && (
                      <div className="mt-2 flex items-center gap-2">
                        <input
                          type="email"
                          value={emailAddress}
                          onChange={(e) => setEmailAddress(e.target.value)}
                          placeholder="recipient@email.com"
                          className="px-2 py-1 bg-gray-700 border border-gray-600 rounded text-white text-xs w-48 focus:outline-none focus:ring-1 focus:ring-blue-500"
                          onKeyDown={(e) => e.key === 'Enter' && handleSendEmail(v._id)}
                        />
                        <button
                          onClick={() => handleSendEmail(v._id)}
                          disabled={emailSending}
                          className="px-2 py-1 bg-blue-600 hover:bg-blue-700 text-white text-xs rounded flex items-center gap-1 disabled:opacity-50"
                        >
                          {emailSending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Send className="h-3 w-3" />}
                          Send
                        </button>
                        <button onClick={() => { setSendingEmailFor(null); setEmailAddress(''); }} className="text-gray-500 hover:text-gray-300">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

export default AdminVouchers;
